// Canvas controls: compass rotation + zoom buttons

import { getViewer } from './state'
import {
  canvasControls,
  compassBtn,
  compassInner,
  zoomInBtn,
  zoomOutBtn
} from './elements'

let animationFrameId: number | null = null
let currentAngleDeg = 0 // Cumulative angle to avoid wrapping jumps

/**
 * Initialize canvas controls (compass + zoom buttons)
 * Call after viewer is ready.
 */
export function initCanvasControls(): void {
  // Show the controls
  canvasControls.style.display = 'flex'

  // Compass click: reset north
  compassBtn.addEventListener('click', () => {
    const viewer = getViewer()
    viewer?.resetNorth()
  })

  // Zoom buttons
  zoomInBtn.addEventListener('click', () => {
    const viewer = getViewer()
    viewer?.zoomIn()
    updateZoomButtons()
  })

  zoomOutBtn.addEventListener('click', () => {
    const viewer = getViewer()
    viewer?.zoomOut()
    updateZoomButtons()
  })

  // Start the compass rotation update loop
  startCompassLoop()
}

/**
 * Show or hide canvas controls (e.g., hide during walk mode)
 */
export function setCanvasControlsVisible(visible: boolean): void {
  canvasControls.style.display = visible ? 'flex' : 'none'
}

/**
 * Update zoom button disabled states based on current zoom limits
 */
function updateZoomButtons(): void {
  const viewer = getViewer()
  if (!viewer) return

  const limits = viewer.getZoomLimits()
  zoomInBtn.disabled = limits.atMin
  zoomOutBtn.disabled = limits.atMax
}

/**
 * Start the compass rotation animation loop
 * Updates compass CSS transform each frame in 3D mode
 */
function startCompassLoop(): void {
  const update = () => {
    const viewer = getViewer()
    if (viewer) {
      const azimuth = viewer.getAzimuthalAngle()
      const gardenRotation = viewer.getGardenRotation()
      const targetDeg = (azimuth + gardenRotation) * (180 / Math.PI)

      // Calculate shortest-path delta to avoid wrapping jumps at ±180°
      let delta = targetDeg - (currentAngleDeg % 360)
      if (delta > 180) delta -= 360
      if (delta < -180) delta += 360
      currentAngleDeg += delta

      compassInner.style.transform = `rotate(${currentAngleDeg}deg)`

      // Update zoom button states periodically
      updateZoomButtons()
    }

    animationFrameId = requestAnimationFrame(update)
  }

  animationFrameId = requestAnimationFrame(update)
}

/**
 * Stop the compass loop (for cleanup)
 */
export function disposeCanvasControls(): void {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
}
